import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateContextKey } from '../utils/context-key';
import { useAnonymousChat } from './useAnonymousChat';

type UseContextBasedChatParams = {
  organizationId: string;
  agentId: string;
  agentVariables: Record<string, unknown>;
  isEmbedded: boolean;
  isWidgetInitialized: boolean;
  userToken: string | null;
  isLoadingToken: boolean;
  onCreateChat: () => Promise<string>;
};

type UseContextBasedChatResult = {
  chatId: string | null;
  setChatId: (id: string) => void; // Only accepts string - use clearChatAndStorage() to clear
  clearChatAndStorage: () => void;
  contextKey: string;
};

export const useContextBasedChat = ({
  organizationId,
  agentId,
  agentVariables,
  isEmbedded,
  isWidgetInitialized,
  userToken,
  isLoadingToken,
  onCreateChat
}: UseContextBasedChatParams): UseContextBasedChatResult => {
  const [chatIds, setChatIds] = useState<Record<string, string>>({});

  const { getStoredChatId, setStoredChatId, clearStoredChatId } = useAnonymousChat();

  const contextKey = useMemo(
    () => generateContextKey(organizationId, agentId, agentVariables),
    [organizationId, agentId, agentVariables]
  );

  const chatId = chatIds[contextKey] ?? null;

  const isInitialized = useMemo(() => {
    if (!isEmbedded) return true;
    return isWidgetInitialized;
  }, [isEmbedded, isWidgetInitialized]);

  const setAndStoreChat = useCallback(
    (id: string | null, key: string) => {
      if (id) {
        setChatIds((prev) => ({ ...prev, [key]: id }));
        if (!userToken) setStoredChatId(id, key);
      } else {
        setChatIds((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        clearStoredChatId(key);
      }
    },
    [userToken, setStoredChatId, clearStoredChatId]
  );

  useEffect(() => {
    if (isLoadingToken || !isInitialized || !contextKey) {
      return;
    }

    if (chatId) {
      return;
    }

    if (!userToken) {
      const storedChatId = getStoredChatId(contextKey);
      if (storedChatId) {
        setAndStoreChat(storedChatId, contextKey);
        return;
      }
    }

    let cancelled = false;

    onCreateChat()
      .then((newChatId) => {
        if (!cancelled) {
          setAndStoreChat(newChatId, contextKey);
        }
      })
      .catch((error: unknown) => {
        console.error('[useContextBasedChat] Failed to create chat:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isLoadingToken,
    isInitialized,
    contextKey,
    chatId,
    userToken,
    onCreateChat,
    getStoredChatId,
    setAndStoreChat
  ]);

  const clearChatAndStorage = useCallback(() => {
    setAndStoreChat(null, contextKey);
  }, [contextKey, setAndStoreChat]);

  const setChatId = useCallback(
    (id: string) => setAndStoreChat(id, contextKey),
    [contextKey, setAndStoreChat]
  );

  return {
    chatId,
    setChatId,
    clearChatAndStorage,
    contextKey
  };
};
